import { Injectable, Injector, NgZone } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { LocalDatabaseService } from '../storage/local-database.service';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { LoggerService } from '../utils/logger.service';
import type { QuestionAnswerEvent } from '../rest/rest.service';
import { RestService } from '../rest/rest.service';
import type {
  CourseGoalSetPayload,
  FlagSetPayload,
  NoteUpdatedPayload,
  PendingEventRow,
  QuestionSeenPayload,
  SubjectResetPayload,
  SyncEventType,
  SyncPullResponse,
  SyncPushResult,
  TestAnswerPayload,
  TestFinishedPayload,
  TestStartedPayload,
} from './sync-event.types';

const MAX_ATTEMPTS = 8;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const SYNC_CURSOR_KEY = 'sync_cursor';

/**
 * Offline outbox (PR4 / 3b): queue progress events in SQLite, flush via POST /api/sync/push when online.
 */
@Injectable({ providedIn: 'root' })
export class ProgressSyncService {
  private started = false;
  private flushing = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private localDb: LocalDatabaseService,
    private connectivity: ConnectivityService,
    private zone: NgZone,
    private injector: Injector
  ) {}

  get enabled(): boolean {
    return isCapacitorNative();
  }

  shouldDeferToOutbox(): boolean {
    return this.enabled && !this.connectivity.isOnline;
  }

  async start(): Promise<void> {
    if (!this.enabled || this.started) return;
    this.started = true;
    await this.localDb.init();
    this.connectivity.connected$.subscribe((online) => {
      if (online) {
        void this.flush('network-up');
        void this.pullServerChanges('network-up');
      }
    });
    if (this.connectivity.isOnline) {
      void this.flush('startup');
      void this.pullServerChanges('startup');
    }
    LoggerService.log('[ProgressSyncService] started');
  }

  async enqueueQuestionSeen(userId: number, questionId: number): Promise<void> {
    const payload: QuestionSeenPayload = { userId, questionId };
    await this.enqueue('QUESTION_SEEN', payload);
  }

  async enqueueTestAnswer(event: QuestionAnswerEvent): Promise<void> {
    const payload: TestAnswerPayload = {
      userTestId: event.userTestId!,
      questionId: event.questionId,
      selectedOption: event.selectedOption,
      correct: event.correct,
      examMode: event.examMode,
      answeredAt: event.answeredAt,
    };
    await this.enqueue('TEST_ANSWER', payload);
  }

  async enqueueFlagSet(questionId: number, flagId: number | null, hide: number): Promise<void> {
    const payload: FlagSetPayload = { questionId, flagId, hide };
    await this.enqueue('FLAG_SET', payload);
  }

  async enqueueNoteUpdated(questionId: number, notes: string): Promise<void> {
    const payload: NoteUpdatedPayload = { questionId, notes };
    await this.enqueue('NOTE_UPDATED', payload);
  }

  async enqueueTestStarted(payload: TestStartedPayload): Promise<void> {
    await this.enqueue('TEST_STARTED', payload);
  }

  async enqueueTestFinished(payload: TestFinishedPayload): Promise<void> {
    await this.enqueue('TEST_FINISHED', payload);
  }

  async enqueueCourseGoalSet(payload: CourseGoalSetPayload): Promise<void> {
    await this.enqueue('COURSE_GOAL_SET', payload);
  }

  async enqueueSubjectReset(payload: SubjectResetPayload): Promise<void> {
    await this.enqueue('SUBJECT_RESET', payload);
  }

  async pendingCount(): Promise<number> {
    if (!this.enabled) return 0;
    return this.localDb.countPendingEvents();
  }

  async flush(reason = 'manual'): Promise<void> {
    if (!this.enabled || !this.connectivity.isOnline || this.flushing) return;
    this.flushing = true;
    await this.localDb.setSyncState('syncing');
    const rest = this.injector.get(RestService);
    let hadError = false;

    try {
      const batch = await this.localDb.listPendingEvents(50);
      if (batch.length === 0) {
        await this.localDb.setSyncState('idle', { clearError: true });
        return;
      }
      LoggerService.log('[ProgressSyncService] flushing', { reason, count: batch.length });

      const usedBatchApi = await this.flushBatchPush(rest, batch);
      if (!usedBatchApi) {
        for (const row of batch) {
          await this.localDb.markEventSyncing(row.eventId);
          try {
            await this.dispatchEventLegacy(rest, row.eventType, row.payload);
            await this.localDb.markEventDone(row.eventId);
          } catch (e) {
            hadError = true;
            await this.markFailed(row, e);
          }
        }
      } else {
        hadError = (await this.localDb.countPendingEvents()) > 0;
      }

      const remaining = await this.localDb.countPendingEvents();
      if (remaining > 0 && hadError) {
        await this.localDb.setSyncState('error', { lastError: 'Some events failed to sync' });
        this.scheduleRetry();
      } else if (remaining > 0) {
        void this.flush('batch-continue');
      } else {
        await this.localDb.setSyncState('idle', { clearError: true });
      }
    } finally {
      this.flushing = false;
    }
  }

  private async flushBatchPush(rest: RestService, batch: PendingEventRow[]): Promise<boolean> {
    try {
      const hasLocalTestReferences = batch.some((row) => {
        if (row.eventType !== 'TEST_STARTED' && row.eventType !== 'TEST_ANSWER' && row.eventType !== 'TEST_FINISHED') {
          return false;
        }
        const payload = JSON.parse(row.payload) as Record<string, unknown>;
        return Number(payload['clientUserTestId'] ?? payload['userTestId'] ?? 0) < 0;
      });
      // Local test IDs need ordered client-side mapping to the server-created ID.
      if (hasLocalTestReferences) return false;
      for (const row of batch) {
        await this.localDb.markEventSyncing(row.eventId);
      }
      const events = batch.map((row) => ({
        eventId: row.eventId,
        eventType: row.eventType,
        payload: JSON.parse(row.payload) as unknown,
      }));
      const response = await firstValueFrom(rest.syncPush(events));
      const byId = new Map<string, SyncPushResult>(
        (response.results ?? []).map((r) => [r.eventId, r])
      );
      for (const row of batch) {
        const result = byId.get(row.eventId);
        if (!result) {
          await this.markFailed(row, new Error('No result for event from sync push'));
          continue;
        }
        if (result.status === 'applied' || result.status === 'duplicate') {
          await this.localDb.markEventDone(row.eventId);
        } else {
          await this.markFailed(row, new Error(result.error ?? 'sync push failed'));
        }
      }
      return true;
    } catch (e) {
      LoggerService.log('[ProgressSyncService] batch push unavailable, falling back to REST', e);
      for (const row of batch) {
        await this.localDb.resetEventToPending(row.eventId);
      }
      return false;
    }
  }

  private async pullServerChanges(reason: string): Promise<void> {
    const rest = this.injector.get(RestService);
    try {
      const since = (await this.localDb.getSyncMeta(SYNC_CURSOR_KEY)) ?? '0';
      const pull = await firstValueFrom(rest.syncPull(since));
      if (pull.cursor) {
        await this.localDb.setSyncMeta(SYNC_CURSOR_KEY, pull.cursor);
      }
      await this.localDb.mergePullResponse(pull);
      LoggerService.log('[ProgressSyncService] pull merged', {
        reason,
        notes: pull.notes?.length ?? 0,
        flags: pull.flags?.length ?? 0,
        answers: pull.testAnswers?.length ?? 0,
        finished: pull.testsFinished?.length ?? 0,
      });
    } catch (e) {
      LoggerService.log('[ProgressSyncService] pull skipped', e);
    }
  }

  private async markFailed(row: PendingEventRow, e: unknown): Promise<void> {
    const msg = e instanceof Error ? e.message : String(e);
    const attempts = row.attemptCount + 1;
    await this.localDb.markEventFailed(row.eventId, msg, attempts);
    if (attempts >= MAX_ATTEMPTS) {
      LoggerService.error('[ProgressSyncService] event dropped after max attempts', {
        eventId: row.eventId,
        type: row.eventType,
      });
    }
  }

  private async enqueue(eventType: SyncEventType, payload: unknown): Promise<void> {
    if (!this.enabled) return;
    await this.localDb.init();
    const eventId = crypto.randomUUID();
    await this.localDb.insertPendingEvent(eventId, eventType, JSON.stringify(payload));
    LoggerService.log('[ProgressSyncService] enqueued', { eventType, eventId });
    if (this.connectivity.isOnline) {
      void this.flush('after-enqueue');
    }
  }

  private async dispatchEventLegacy(
    rest: RestService,
    eventType: SyncEventType,
    payloadJson: string
  ): Promise<void> {
    const payload = JSON.parse(payloadJson) as Record<string, unknown>;
    switch (eventType) {
      case 'QUESTION_SEEN': {
        const p = payload as unknown as QuestionSeenPayload;
        await firstValueFrom(rest.syncMarkQuestionSeen(p.userId, p.questionId));
        return;
      }
      case 'TEST_ANSWER': {
        const p = payload as unknown as TestAnswerPayload;
        const userTestId = await this.resolveUserTestId(p.userTestId);
        const event: QuestionAnswerEvent = {
          userTestId,
          questionId: p.questionId,
          selectedOption: p.selectedOption,
          correct: p.correct,
          examMode: p.examMode ?? false,
          answeredAt: p.answeredAt,
        };
        await firstValueFrom(rest.syncSaveQuestionAnswer(event));
        return;
      }
      case 'FLAG_SET': {
        const p = payload as unknown as FlagSetPayload;
        const ok = await rest.syncSaveQuestionState(p.questionId, p.flagId, p.hide);
        if (!ok) {
          throw new Error('syncSaveQuestionState failed');
        }
        return;
      }
      case 'NOTE_UPDATED': {
        const p = payload as unknown as NoteUpdatedPayload;
        const ok = await rest.syncSaveQuestionNotes(p.questionId, p.notes);
        if (!ok) {
          throw new Error('syncSaveQuestionNotes failed');
        }
        return;
      }
      case 'TEST_STARTED': {
        const p = payload as unknown as TestStartedPayload;
        const id = await rest.syncCreateUserTest(p);
        if (id == null) {
          throw new Error('syncCreateUserTest failed');
        }
        if (p.clientUserTestId != null && p.clientUserTestId < 0) {
          await this.localDb.setSyncMeta(this.testIdMapKey(p.clientUserTestId), String(id));
        }
        return;
      }
      case 'TEST_FINISHED': {
        const p = payload as unknown as TestFinishedPayload;
        const userTestId = await this.resolveUserTestId(p.userTestId);
        const ok = await rest.syncUpdateUserTest(
          userTestId,
          p.percentage,
          p.passed === 1,
          p.timeEnd
        );
        if (!ok) {
          throw new Error('syncUpdateUserTest failed');
        }
        return;
      }
      case 'COURSE_GOAL_SET': {
        const p = payload as unknown as CourseGoalSetPayload;
        const goal = await rest.syncSaveCourseGoal({
          courseId: p.courseId,
          targetDate: p.targetDate,
          leaderboardOptIn: p.leaderboardOptIn ?? undefined,
        });
        if (!goal) {
          throw new Error('syncSaveCourseGoal failed');
        }
        return;
      }
      case 'SUBJECT_RESET': {
        const p = payload as unknown as SubjectResetPayload;
        const ok = await rest.syncResetSubject(p.subjectId);
        if (!ok) {
          throw new Error('syncResetSubject failed');
        }
        return;
      }
      default:
        throw new Error(`Unsupported sync event type: ${eventType}`);
    }
  }

  private testIdMapKey(localUserTestId: number): string {
    return `local_test_id:${localUserTestId}`;
  }

  private async resolveUserTestId(userTestId: number): Promise<number> {
    if (userTestId >= 0) return userTestId;
    const mapped = await this.localDb.getSyncMeta(this.testIdMapKey(userTestId));
    const serverId = Number(mapped);
    if (!mapped || !Number.isFinite(serverId) || serverId <= 0) {
      throw new Error(`Server test ID is not available for local test ${userTestId}`);
    }
    return serverId;
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    void this.localDb.getSyncMeta('next_backoff_ms').then((raw) => {
      const prev = raw ? Number(raw) : BASE_BACKOFF_MS;
      const delay = Math.min(Math.max(prev * 2, BASE_BACKOFF_MS), MAX_BACKOFF_MS);
      const jitter = Math.floor(Math.random() * 400);
      void this.localDb.setSyncMeta('next_backoff_ms', String(delay));
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.zone.run(() => void this.flush('backoff'));
      }, delay + jitter);
    });
  }
}
